import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { UrlBuilder } from '../../../../core';
import { ReportData } from '../../interfaces/report-data';
import { IAction } from '../../interfaces/report-grid';
import { DocumentDownloader } from '../../../../documents';
import { SampleReportInfo } from '../../../../documents/models/sample-report-info';
import { ActivityLogger } from '../../../../activities/activity-logger';
import { ReportUpdateRequest } from '../../interfaces/report-update-request';
import { ReportsStorageService } from '../../services/reports-storage-service';
import { Observable } from 'rxjs';
import { SampleTrackingDialogResult, SampleTrackingResultAction } from '../../../service/dialog-service/sample-tracking-result';
import { SampleTrackingDialogService } from '../../../service/dialog-service/sample-tracking-dialog-service';
import { map, switchMap } from 'rxjs/operators';
import * as moment from 'moment';
import { DatePipe } from '@angular/common';


@Component({
    selector: 'report-action-renderer',
    template: require('./action-renderer.component.html'),
    styles: [require('./action-renderer.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportActionRendererComponent implements OnInit {

    @Input()
    public actions: IAction[] = [];

    @Input()
    public data: ReportData;

    @Output()
    public reloadData: EventEmitter<boolean> = new EventEmitter<false>();

    @Output()
    public reloadGeneratedData: EventEmitter<boolean> = new EventEmitter<false>();

    @Output()
    public updateReadDataFormat: EventEmitter<string> = new EventEmitter<string>();

    public enable: boolean;

    public notify: boolean;

    private messageText: string;

    private isSuccess: boolean;

    constructor(
        private storage: ReportsStorageService,
        private documentDownloader: DocumentDownloader,
        private activityLogger: ActivityLogger,
        private dialogService: SampleTrackingDialogService,
        private urlBuilder: UrlBuilder,
    ) { }

    public ngOnInit() {
        if (!this.data) { return; }
        this.enable = this.data.activeFlag;
        this.notify = this.data.notifyFlag;
    }

    public handleNgClass(enableToggleClass = false, isDownloadAction = false) {
        if (isDownloadAction && this.data.recordCount === 0) {
            return 'report-action-disabled';
        }
        if (this.data && this.data.reportStatus && !enableToggleClass) {
            if (this.data.reportStatus.toLowerCase().indexOf('completed') !== -1) {
                this.enable = this.data.activeFlag;
                return 'report-action';
            }
            this.enable = this.data.activeFlag;
            return 'report-action-disabled';
        }
        if (enableToggleClass && this.hideToggle()) {
            this.enable = false;
            return 'enable-toggle-disabled-visible';
        }
        this.enable = this.data.activeFlag;
        return 'report-action';
    }

    public downloadAsExcel() {
        if (this.data.reportStatus.toLowerCase() !== 'completed') { return; }
        let reportInfo: SampleReportInfo = {
            reportId: this.data.reportID,
            fileName: this.data.fileName + '.xlsx',
            folderName: this.data.folderName,
            storageLocation: this.data.storageLocation,
            categoryNaturalKey: 'sample-report'
        };
        this.documentDownloader.downloadSampleReportByUrl(reportInfo);
        this.updateReportReadStatus();
    }

    public downloadAsCsv() {
        if (this.data.reportStatus.toLowerCase() !== 'completed') { return; }
        let reportInfo: SampleReportInfo = {
            reportId: this.data.reportID,
            fileName: this.data.fileName + '.csv',
            folderName: this.data.folderName,
            storageLocation: this.data.storageLocation,
            categoryNaturalKey: 'sample-report'
        };
        this.documentDownloader.downloadSampleReportByUrl(reportInfo);
        this.updateReportReadStatus();
    }

    public editReport() {
        this.activityLogger.logActivity('Edit Scheduled/Saved Report - Sample Tracking');

        this.urlBuilder.esa().sampletracking().
            report().NewGenerateReport(0).
            navigate({ queryParams: { reportId: this.data.reportID } });
    }

    public async deleteReport() {
        const result = await this.showDeleteDialog().toPromise();
        if (result.action === SampleTrackingResultAction.close) {
            return;
        }

        (await this.storage.deleteExistingReport(Number(this.data.reportID))).toPromise()
            .then(() => {
                this.isSuccess = true;
                this.messageText = this.data.scheduledReportName + ' has been deleted!';
                this.openDialog();
                this.reloadData.emit(true);
            })
            .catch(() => {
                this.isSuccess = false;
                this.messageText = this.data.scheduledReportName + ' not deleted!';
                this.openDialog();
                this.reloadData.emit(false);
            });
        this.activityLogger.logActivity('Delete Scheduled/Saved Report - Sample Tracking');
    }

    public async enableReport() {
        this.enable = !this.enable;
        await this.updateReport(true);
    }

    public async notifyReport() {
        this.notify = !this.notify;
        await this.updateReport(false);
    }

    public async runReport() {
        let reportRunTime = `${new DatePipe('en-US').transform(moment().local(), 'dd-MMM-yy hh:mm:ss a')} ${Intl.DateTimeFormat().resolvedOptions().timeZone }`;
        let reportContent = this.data.scheduledReportContent;
        let filterContent = this.data.filterContent.replace(/\\/g, '');
        (await this.storage.runReport(Number(this.data.reportID), reportRunTime, reportContent, filterContent)).toPromise()
            .then(() => {
                this.isSuccess = true;
                this.messageText = this.data.scheduledReportName + ' has been queued';
                this.openDialog();
                this.reloadData.emit(false);
                this.reloadGeneratedData.emit(true);
            })
            .catch(() => {
                this.isSuccess = false;
                this.messageText = this.data.scheduledReportName + ' not queued';
                this.openDialog();
                this.reloadData.emit(false);
            });
        this.activityLogger.logActivity('Run Scheduled/Saved Report - Sample Tracking');
    }

    public hideToggle() {
        return this.enable && !this.data.scheduledContent;
    }

    private async updateReport(reloadData: boolean) {
        var request: ReportUpdateRequest = {
            reportID: Number(this.data.reportID),
            isEnabled: this.enable,
            isNotify: this.notify,
            reportRunTime: `${moment().local().format('DD-MMM-YY hh:mm:ss A')} ${Intl.DateTimeFormat().resolvedOptions().timeZone}`
        };
        (await this.storage.updateReportActivity(request)).toPromise()
            .then(() => {
                this.reloadData.emit(reloadData);
            })
            .catch(() => {
                this.reloadData.emit(false);
            });
    }

    private showDeleteDialog(): Observable<SampleTrackingDialogResult> {
        return this.dialogService.showDeleteReportDialog(this.data).pipe(
            switchMap(dialog => dialog.onHide()),
            map(dialogResult => dialogResult || { action: SampleTrackingResultAction.close })
        );
    }

    private openDialog() {
        this.dialogService.showPostActionDialogue(this.messageText, this.isSuccess, '0px');
    }

    private async updateReportReadStatus() {
        if (this.data.isRead) {
            return;
        }
        (await this.storage.updateReportReadStatus(Number(this.data.id))).toPromise()
            .then(() => {
                this.reloadData.emit(true);
                this.updateReadDataFormat.emit(this.data.reportID);
            })
            .catch(() => {
                this.reloadData.emit(false);
            });
    }
}